import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { uipApiInstance } from '../../services';
import { logout } from '../auth/authSlice';
import { logEventToBackEnd } from '../global/globalSlice';
import moment from 'moment';

// export type FinancialAdvisor = {
//     id?: any;
//     advisorName: string;
//     advisorEmail: string;
//     advisorPhoneNumber: string;
//     numberOfClients: string;
//     numberOfGoals: string;
//     createdOn: string;
// };

export type ClientsDataType = {
    id?: any;
    advisorEmail: string;
    lastModified: string | number;
    clientFirstName: string;
    clientLastName: string;
    advisorName: string;
    clientEmail: string;
    goals: any;
    portfolioValue: string;
    clientFullName: string;
    advisorId: string;
};

type InitialState = {
    // financialAdvisors: Array<any>;
    advisorEmailId: string;
    clients: Array<ClientsDataType>;
    // admins: Array<any>;
    users: Array<any>;
    totoalPages?: number;
    totalUserCount?: number;
    loading: boolean;
    error?: null | undefined | any;
    showFinancialAdvisorRemoveModal: boolean;
    triggeredFinancialAdvisorRemoval: boolean;
    isUpdatingEULA?: boolean;
    selectedUser?: {
        id: string;
        advisorName?: string;
        advisorEmail?: string;
        advisorPhoneNumber?: string;
        numberOfClients?: number;
        numberOfGoals?: string | number;
        adminName?: string;
        adminEmail?: string;
        userType: string;
        adminPhoneNumber?: string;
        createdOn?: string;
    } | null;
    breadcrumbLabel: string;
    advisors?: Array<any>;
};

const initialState: InitialState = {
    // financialAdvisors: [],
    advisorEmailId: '',
    clients: [],
    // admins: [],
    users: [],
    totoalPages: 0,
    totalUserCount: 0,
    loading: false,
    error: null,
    showFinancialAdvisorRemoveModal: false,
    triggeredFinancialAdvisorRemoval: false,
    isUpdatingEULA: false,
    selectedUser: null,
    breadcrumbLabel: '',
    advisors: []
};

type assignAdvisorToClientInput = {
    clientId: string;
    userId: string;
};

export const assignAdvisorToClient = createAsyncThunk(
    'admin/assignAdvisorToClient',
    async ({ clientId, userId }: assignAdvisorToClientInput, { dispatch, rejectWithValue }) => {
        try {
            await uipApiInstance({
                method: 'POST',
                url: '/api/advisorportal/associateclientgoalstofa',
                withCredentials: false,
                data: { clientId, userId }
            });

            await dispatch(fetchAllClients());

            return true;
        } catch (err) {
            console.log(err);
            dispatch(logEventToBackEnd('LINK_CLIENTS_TO_FA_API_ERROR'));
            return rejectWithValue(err);
        }
    }
);

export const uploadPortfolioFile = createAsyncThunk(
    'admin/uploadPortfolioFile',
    async (
        {
            file,
            tenant,
            weight_type,
            tenant_supported_weight_type,
            is_ft_fullfilled,
            portfolioBundleName,
            goalCategory
        }: {
            file: File | null;
            tenant: string;
            weight_type: string;
            tenant_supported_weight_type: string;
            is_ft_fullfilled: boolean;
            portfolioBundleName?: string;
            goalCategory?: string;
        },
        { dispatch, rejectWithValue }
    ) => {
        try {
            const formData = new FormData();
            if (file) {
                formData.append('file', file);
                formData.append('tenant', tenant);
                formData.append('tenant_supported_weight_type', tenant_supported_weight_type);
                formData.append('weight_type', weight_type);
                formData.append('is_ft_fullfilled', JSON.stringify(is_ft_fullfilled));
                if (tenant === 'banortemex') {
                    formData.append('portfolioBundleName', portfolioBundleName || '');
                    formData.append('goalCategory', goalCategory || '');
                }
                const response = await uipApiInstance({
                    method: 'POST',
                    url: '/api/advice/validate_upload_portfolio',
                    withCredentials: false,
                    data: formData
                });
                // const response = await axios({
                //     method: 'POST',
                //     baseURL: process.env.REACT_APP_UIP_API_URL,
                //     url: '/api/advice/validate_upload_portfolio',
                //     withCredentials: false,
                //     headers: {
                //         Authorization: 'Bearer ' + accessToken,
                //         'Content-Type': 'multipart/form-data',
                //         'Cache-Control': 'no-cache, no-store, must-revalidate'
                //     },
                //     data: formData
                // });
                // TODO:: Clean if not required, After successful upload, refresh the portfolio list
                // await dispatch(getPortfolioList());
                return response.data;
            }
        } catch (err: any) {
            console.log(err);
            dispatch(logEventToBackEnd('UPLOAD_PORTFOLIO_FILE_API_ERROR'));
            if (err.response && err.response.status === 400) {
                return err.response.data;
            }
            return rejectWithValue(err);
        }
    }
);

export const fetchAllClients = createAsyncThunk('admin/fetchAllClients', async (_, { dispatch, rejectWithValue }) => {
    try {
        const response = await uipApiInstance({
            method: 'GET',
            url: '/api/advisorportal/getclients',
            withCredentials: false
        });

        const formattedData = response.data?.data?.map((d: any) => ({
            id: d._id,
            advisorName: d.advisorName || 'N/A',
            advisorId: d.userId,
            clientFullName: d.firstName + (d.lastName ? ' ' + d.lastName : ''),
            clientEmail: d.email,
            goals: d.goals,
            portfolioValue: d.portfolioValue,
            lastModified: d.lastUpdatedTime
        }));

        return formattedData;
    } catch (err) {
        console.log(err);
        dispatch(logEventToBackEnd('GET_CLIENTS_API_ERROR'));
        return rejectWithValue(err);
    }
});

const sortUsers = (a: any, b: any) => moment(a?.__meta?.timestamp).valueOf() - moment(b?.__meta?.timestamp).valueOf();

export const fetchAdminsAndAdvisors = createAsyncThunk(
    'admin/fetchAdminsAndAdvisors',
    async (
        {
            page = 1,
            page_size = 10,
            search_term = '',
            selected_role = 'allroles',
            updateState = true
        }: {
            page?: number;
            page_size?: number;
            search_term?: string;
            selected_role?: string;
            updateState?: boolean;
        } = {},
        { getState, dispatch, rejectWithValue }
    ) => {
        try {
            const state: any = getState();
            const tenantId = state?.auth.user?.tenant;
            let payload: { noCache: boolean; tenant: any; role?: string } = {
                noCache: true,
                tenant: tenantId
            };

            let url =
                `/api/advice/getusers?page=${page}&page_size=${page_size}` +
                (search_term ? `&search_term=${encodeURIComponent(search_term)}` : '');

            if (selected_role && selected_role !== 'allroles') {
                payload = { ...payload, role: selected_role };
            }

            const response = await uipApiInstance({
                method: 'POST',
                url: url,
                withCredentials: false,
                data: {
                    ...payload
                }
            });

            const roleHierarchy = ['ftadmin', 'admin', 'advisor'];
            const fomatUserData = (data: any, tenant: any, role: any) => {
                return data.sort(sortUsers).map((user: any) => ({
                    id: user.__meta.id,
                    userName: user.name,
                    userEmail: user.email,
                    userPhoneNumber: user.phoneNumber,
                    userTenant: tenant,
                    userType: role,
                    createdOn: user.createdOn,
                    userActive: user?.active,
                    okta_login_status: user?.okta_login_status
                }));
            };

            let finalUsers: any = [];
            for (let eachtenant in response.data) {
                let tenantData = response.data?.[eachtenant] || [];
                for (let role of roleHierarchy) {
                    let roleData = tenantData?.[role] || [];
                    let tempUsersList = fomatUserData(roleData, eachtenant, role);
                    finalUsers = [...finalUsers, ...tempUsersList];
                }
            }
            const restructuredData = {
                admins: [],
                financialAdvisors: [],
                users: finalUsers,
                updatedState: updateState,
                totoalPages: response.data?.total_pages || 0,
                totalUserCount: response.data?.total_users || 0
            };
            return restructuredData;
        } catch (err) {
            console.log(err);
            dispatch(logEventToBackEnd('GET_USERS_API_ERROR'));
            return rejectWithValue(err);
        }
    }
);

export const fetchAllAdvisors = createAsyncThunk(
    'admin/fetchAllAdvisors',
    async (
        { page_size = 100, search_term = '' }: { page_size?: number; search_term?: string } = {},
        { getState, dispatch, rejectWithValue }
    ) => {
        try {
            const state: any = getState();
            const tenantId = state?.auth.user?.tenant;

            const fetchPage = async (page: number) => {
                const url =
                    `/api/advice/getusers?page=${page}&page_size=${page_size}` +
                    (search_term ? `&search_term=${encodeURIComponent(search_term)}` : '');
                const response = await uipApiInstance({
                    method: 'POST',
                    url,
                    withCredentials: false,
                    data: { noCache: true, tenant: tenantId, role: 'advisor' }
                });
                return response.data;
            };

            const formatAdvisors = (data: any) => {
                let advisors: any[] = [];
                for (const eachtenant in data) {
                    const advisorData = data?.[eachtenant]?.advisor || [];
                    advisors = [
                        ...advisors,
                        ...advisorData.sort(sortUsers).map((user: any) => ({
                            id: user.__meta.id,
                            userName: user.name,
                            userEmail: user.email,
                            userPhoneNumber: user.phoneNumber,
                            userTenant: eachtenant,
                            userType: 'advisor',
                            createdOn: user.createdOn,
                            userActive: user?.active,
                            okta_login_status: user?.okta_login_status
                        }))
                    ];
                }
                return advisors;
            };

            // Fetch page 1 first to learn total_pages, then loop the remaining pages.
            const firstPage = await fetchPage(1);
            const totalPages = firstPage?.total_pages || 1;
            let allAdvisors = formatAdvisors(firstPage);

            for (let page = 2; page <= totalPages; page++) {
                const pageData = await fetchPage(page);
                allAdvisors = [...allAdvisors, ...formatAdvisors(pageData)];
            }

            // Keep the first occurrence per userName so the list stays unique across pages.
            const uniqueAdvisors = Array.from(
                new Map(allAdvisors.map((advisor) => [advisor.userName, advisor])).values()
            );

            return {
                advisors: uniqueAdvisors,
                totalPages,
                totalUserCount: firstPage?.total_users ?? uniqueAdvisors.length
            };
        } catch (err) {
            console.log(err);
            dispatch(logEventToBackEnd('GET_USERS_API_ERROR'));
            return rejectWithValue(err);
        }
    }
);

export const refreshOktaStatus = createAsyncThunk(
    'admin/refreshOktaStatus',
    async ({ userId, fromTenant }: { userId: string; fromTenant: string }, { dispatch, rejectWithValue, getState }) => {
        try {
            const response = await uipApiInstance({
                method: 'POST',
                url: '/api/advice/refresh_okta_status',
                withCredentials: false,
                data: {
                    user_id: userId,
                    from_tenant: fromTenant
                }
            });

            if (response.data?.success) {
                // update the particular user in the state
                const state: any = getState();
                const updatedUsers = state.advisorAdmin.users.map((user: any) =>
                    user.id === userId ? { ...user, okta_login_status: response.data.status } : user
                );
                return updatedUsers;
            }

            return rejectWithValue('Failed to refresh OKTA status');
        } catch (err) {
            console.error(err);
            dispatch(logEventToBackEnd('REFRESH_OKTA_STATUS_API_ERROR'));
            return rejectWithValue(err);
        }
    }
);

export const transferPlans = createAsyncThunk(
    'admin/transferPlans',
    async ({ fromUser, toUser }: { fromUser: string; toUser: string }, { getState, dispatch, rejectWithValue }) => {
        try {
            const state: any = getState();
            const tenantId = state?.auth.user?.tenant;

            const response = await uipApiInstance({
                method: 'POST',
                url: '/api/advice/transferclientprofile',
                withCredentials: false,
                data: {
                    from_user: fromUser,
                    to_user: toUser,
                    from_tenant: tenantId
                }
            });

            return response.data;
        } catch (err: any) {
            console.error('TRANSFER_PLANS_ERROR', err);
            dispatch(logEventToBackEnd('TRANSFER_PLANS_API_ERROR'));
            return rejectWithValue(err.response?.data || { message: 'Plan transfer failed' });
        }
    }
);

export const getDashboardMetrics = createAsyncThunk('admin/metrics', async (_, { dispatch, rejectWithValue }) => {
    try {
        const response = await uipApiInstance({
            method: 'GET',
            url: '/api/advisorportal/metrics',
            withCredentials: false
        });

        return response.data;
    } catch (err) {
        console.log(err);
        dispatch(logEventToBackEnd('ADMIN_METRICS_API_ERROR'));
        return rejectWithValue(err);
    }
});

export const resetPassword = createAsyncThunk(
    'admin/resetPassowrd',
    async ({ userName, tenant }: { userName: string; tenant: string }, { dispatch, rejectWithValue }) => {
        try {
            const response = await uipApiInstance({
                method: 'POST',
                url: '/api/advice/reset_password',
                withCredentials: false,
                data: { username: userName, tenant: tenant }
            });

            return response.data;
        } catch (error) {
            console.error(error);
            dispatch(logEventToBackEnd('ADMIN_RESET_PASSWORD_API_ERROR'));
            return rejectWithValue(error);
        }
    }
);

export const activateUser = createAsyncThunk(
    'admin/activateuser',
    async ({ userId, tenant }: { userId: string; tenant: string }, { dispatch, rejectWithValue }) => {
        try {
            const response = await uipApiInstance({
                method: 'POST',
                url: '/api/advice/activateuser',
                withCredentials: false,
                data: { id: userId, tenant }
            });

            return response.data;
        } catch (err) {
            console.log(err);
            dispatch(logEventToBackEnd('ADMIN_ACTIVATE_USER_API_ERROR'));
            return rejectWithValue(err);
        }
    }
);

export const deleteUser = createAsyncThunk(
    'admin/deleteUser',
    async ({ userId, tenant }: { userId: string; tenant: string }, { dispatch, rejectWithValue }) => {
        try {
            const response = await uipApiInstance({
                method: 'POST',
                url: '/api/advisorportal/deleteuser',
                withCredentials: false,
                data: { id: userId, tenant }
            });

            return response.data;
        } catch (err) {
            console.log(err);
            dispatch(logEventToBackEnd('ADMIN_DELETE_USER_API_ERROR'));
            return rejectWithValue(err);
        }
    }
);

export const deactivateUser = createAsyncThunk(
    'admin/deactivateuser',
    async ({ userId, tenant }: { userId: string; tenant: string }, { dispatch, rejectWithValue }) => {
        try {
            const response = await uipApiInstance({
                method: 'POST',
                url: '/api/advice/deactivateuser',
                withCredentials: false,
                data: { id: userId, tenant }
            });

            return response.data;
        } catch (err) {
            console.log(err);
            dispatch(logEventToBackEnd('ADMIN_DELETE_USER_API_ERROR'));
            return rejectWithValue(err);
        }
    }
);

export const updateUser = createAsyncThunk('admin/updateUser', async (data: any, { dispatch, rejectWithValue }) => {
    try {
        const response = await uipApiInstance({
            method: 'POST',
            url: '/api/advice/updateuser',
            withCredentials: false,
            data
        });
        return response.data;
    } catch (err) {
        console.log(err);
        dispatch(logEventToBackEnd('ADMIN_UPDATE_USER_API_ERROR'));
        return rejectWithValue(err);
    }
});

export const updateEula = createAsyncThunk('admin/updateEula', async (data: any, { dispatch, rejectWithValue }) => {
    try {
        const response = await uipApiInstance({
            method: 'POST',
            url: '/api/advisorportal/updateeula',
            withCredentials: false,
            data
        });
        return response.data.success;
    } catch (err) {
        console.log(err);
        dispatch(logEventToBackEnd('UPDATE_EULA_API_ERROR'));
        return rejectWithValue(err);
    }
});

export const checkIfUserExists = createAsyncThunk(
    'admin/checkIfUserExists',
    async (
        { emailId, tenantName }: { emailId: string; tenantName: string },
        { dispatch, rejectWithValue, getState }
    ) => {
        try {
            const state: any = getState();

            const tenantId = state?.auth.user?.tenant;
            const userRole = state?.auth.user?.userRole;
            const response = await uipApiInstance({
                method: 'POST',
                url: '/api/advice/userexists',
                withCredentials: false,
                data: {
                    email: emailId,
                    tenant: tenantName && userRole === 'ftadmin' ? tenantName : tenantId
                }
            });

            return response?.data?.data?.exists;
        } catch (error) {
            console.log(error);
            dispatch(logEventToBackEnd('USER_EXISTS_API_ERROR'));
            return rejectWithValue(error);
        }
    }
);

export const fetchAdvisorAdminFAClients = createAsyncThunk(
    'admin/fetchAdvisorAdminFAClients',
    async (financialAdvisorId: string, { dispatch, rejectWithValue }) => {
        try {
            console.log(financialAdvisorId);
            const response = await uipApiInstance({
                method: 'GET',
                url: '/api/advice/getclients',
                withCredentials: false
            });

            const formattedData = response.data?.data
                ?.filter((ufd: any) => ufd?.__meta?.user === financialAdvisorId)
                ?.map((d: any) => ({
                    id: d._id,
                    advisorName: d.advisorName || 'N/A',
                    clientFullName: d.firstName + (d.lastName ? ' ' + d.lastName : ''),
                    clientEmail: d.email,
                    goals: d.goals,
                    portfolioValue: d.portfolioValue,
                    lastModified: d.lastUpdatedTime
                }));

            return formattedData;
        } catch (error) {
            console.log(error);
            dispatch(logEventToBackEnd('GET_CLIENTS_API_ERROR'));
            return rejectWithValue(error);
        }
    }
);

export const getPortfolioList = createAsyncThunk(
    'admin/portfoliolist',
    async ({ tenant }: { tenant: string }, { dispatch, rejectWithValue }) => {
        try {
            const response = await uipApiInstance({
                method: 'GET',
                url: '/api/advice/portfoliolist',
                withCredentials: false,
                params: {
                    tenant
                }
            });

            return response.data;
        } catch (err) {
            console.log(err);
            dispatch(logEventToBackEnd('ADMIN_PORTFOLIOLIST_API_ERROR'));
            return rejectWithValue(err);
        }
    }
);

export const getSamplePortfolio = createAsyncThunk(
    'admin/getSamplePortfolio',
    async (_, { dispatch, rejectWithValue }) => {
        try {
            const response = await uipApiInstance({
                method: 'GET',
                url: '/api/advice/samplePortfolio',
                withCredentials: false
            });

            return response.data;
        } catch (err) {
            console.log(err);
            dispatch(logEventToBackEnd('ADMIN_SAMPLEPORTFOLIO_API_ERROR'));
            return rejectWithValue(err);
        }
    }
);

export const updatePortfolio = createAsyncThunk(
    'admin/updatePortfolio',
    async (data: any, { dispatch, rejectWithValue }) => {
        try {
            const response = await uipApiInstance({
                method: 'POST',
                url: '/api/advice/update_portfolio',
                withCredentials: false,
                data
            });
            return response.data;
        } catch (err) {
            console.log(err);
            dispatch(logEventToBackEnd('ADMIN_UPDATE_USER_API_ERROR'));
            return rejectWithValue(err);
        }
    }
);

export const getPlanTransferList = createAsyncThunk(
    'admin/plantransferlist',
    async (_, { getState, dispatch, rejectWithValue }) => {
        try {
            const state: any = getState();
            const tenant = state?.auth.user?.tenant;
            const response = await uipApiInstance({
                method: 'GET',
                url: `/api/advice/workflowlist?workflow_type=RemappingClient&tenant=${tenant}`,
                withCredentials: false
            });
            return response.data;
        } catch (err) {
            console.log(err);
            dispatch(logEventToBackEnd('ADMIN_PLANTRANSFERLIST_API_ERROR'));
            return rejectWithValue(err);
        }
    }
);

export const updateWorkflow = createAsyncThunk(
    'admin/updateWorkflow',
    async (data: any, { dispatch, rejectWithValue }) => {
        try {
            const response = await uipApiInstance({
                method: 'POST',
                url: '/api/advice/update_workflow',
                withCredentials: false,
                data
            });
            return response.data;
        } catch (err) {
            console.log(err);
            dispatch(logEventToBackEnd('ADMIN_UPDATE_WORKFLOW_API_ERROR'));
            return rejectWithValue(err);
        }
    }
);

const advisorAdminClientsTable = createSlice({
    name: 'advisorAdminClientsState',
    initialState,
    reducers: {
        // updateFinancialAdvisors: (state, action: PayloadAction<any>) => {
        //     state.financialAdvisors = action.payload;
        // },
        updateFinancialAdvisorDeleteModalState: (state, action) => {
            state.showFinancialAdvisorRemoveModal = action.payload;
        },
        updateDeleteId: (state, action) => {
            state.advisorEmailId = action.payload;
        },
        updatedTriggeredFinancialAdvisorRemoval: (state, action) => {
            state.triggeredFinancialAdvisorRemoval = action.payload;
        },
        updateSelectedUser: (state, action: PayloadAction<any>) => {
            state.selectedUser = action.payload;
        },
        updateBreadcrumbLabel: (state, action: PayloadAction<any>) => {
            state.breadcrumbLabel = action.payload;
        },
        updateSelectedFinancialAdvisorClients: (state, action: PayloadAction<any>) => {
            state.clients = action.payload;
        },
        updateUsersByEmail: (state, action: PayloadAction<any>) => {
            state.users.forEach((stateuser) => {
                if (stateuser.userEmail == action.payload.userEmail) {
                    stateuser.userType = action.payload.userType;
                    stateuser.userActive = action.payload.userActive;
                }
            });
        },
        resetAdviceAdminReducer: () => initialState
    },
    extraReducers: (builder) => {
        builder.addCase(logout, () => initialState),
            builder.addCase(updateEula.pending, (state) => {
                state.isUpdatingEULA = true;
            }),
            builder.addCase(updateEula.fulfilled, (state) => {
                state.isUpdatingEULA = false;
            }),
            builder.addCase(updateEula.rejected, (state) => {
                state.isUpdatingEULA = false;
            }),
            builder.addCase(fetchAllClients.pending, (state) => {
                state.loading = true;
            }),
            builder.addCase(fetchAllClients.fulfilled, (state, action) => {
                state.loading = false;
                state.clients = action.payload;
                state.error = null;
            }),
            builder.addCase(fetchAllClients.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            }),
            builder.addCase(assignAdvisorToClient.pending, (state) => {
                state.loading = true;
            }),
            builder.addCase(assignAdvisorToClient.fulfilled, (state) => {
                state.loading = false;
                state.error = null;
            }),
            builder.addCase(assignAdvisorToClient.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            }),
            builder.addCase(fetchAdminsAndAdvisors.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload.updatedState === false) {
                    return;
                }
                state.users = action.payload?.users;
                state.totoalPages = action.payload?.totoalPages;
                state.totalUserCount = action.payload?.totalUserCount;
                state.error = null;
            }),
            builder.addCase(fetchAdminsAndAdvisors.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            }),
            builder.addCase(fetchAllAdvisors.pending, (state) => {
                state.loading = true;
            }),
            builder.addCase(fetchAllAdvisors.fulfilled, (state, action) => {
                state.loading = false;
                state.advisors = action.payload.advisors;
                state.error = null;
            }),
            builder.addCase(fetchAllAdvisors.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            }),
            builder.addCase(fetchAdvisorAdminFAClients.pending, (state) => {
                state.loading = true;
            }),
            builder.addCase(fetchAdvisorAdminFAClients.fulfilled, (state, action) => {
                state.loading = false;
                state.clients = action.payload;
                state.error = null;
            }),
            builder.addCase(fetchAdvisorAdminFAClients.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            }),
            builder.addCase(resetPassword.pending, (state) => {
                state.loading = true;
            }),
            builder.addCase(resetPassword.fulfilled, (state) => {
                state.loading = false;
                state.error = null;
            }),
            builder.addCase(resetPassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            }),
            builder.addCase(activateUser.pending, (state) => {
                state.loading = true;
            }),
            builder.addCase(activateUser.fulfilled, (state) => {
                state.loading = false;
                state.error = null;
            }),
            builder.addCase(activateUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            }),
            builder.addCase(deactivateUser.pending, (state) => {
                state.loading = true;
            }),
            builder.addCase(deactivateUser.fulfilled, (state) => {
                state.loading = false;
                state.error = null;
            }),
            builder.addCase(deactivateUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            }),
            builder.addCase(updateUser.pending, (state) => {
                state.loading = true;
            }),
            builder.addCase(updateUser.fulfilled, (state) => {
                state.loading = false;
                state.error = null;
            }),
            builder.addCase(updateUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            }),
            builder.addCase(uploadPortfolioFile.pending, (state) => {
                state.loading = true;
            }),
            builder.addCase(uploadPortfolioFile.fulfilled, (state) => {
                state.loading = false;
                state.error = null;
            }),
            builder.addCase(uploadPortfolioFile.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

export type GetCMSConfigParams = {
    tenant: string;
    language: string;
};

export const getCMSConfig = createAsyncThunk(
    'admin/getCMSConfig',
    async ({ tenant, language }: GetCMSConfigParams, { dispatch, rejectWithValue }) => {
        try {
            const response = await uipApiInstance({
                method: 'GET',
                url: `/api/advice/getCMSConfig?tenant=${tenant}&language=${language}`,
                withCredentials: false
            });

            return response.data;
        } catch (err) {
            console.log(err);
            dispatch(logEventToBackEnd('GET_CMS_CONFIG_API_ERROR'));
            return rejectWithValue(err);
        }
    }
);

export const updateCMS = createAsyncThunk('admin/updateCMS', async (data: any, { dispatch, rejectWithValue }) => {
    try {
        const response = await uipApiInstance({
            method: 'POST',
            url: '/api/advice/updateCMS',
            withCredentials: false,
            data
        });
        return response.data;
    } catch (err) {
        console.log(err);
        dispatch(logEventToBackEnd('ADMIN_UPDATE_USER_API_ERROR'));
        return rejectWithValue(err);
    }
});

export const {
    // updateFinancialAdvisors,
    updateFinancialAdvisorDeleteModalState,
    updateDeleteId,
    updatedTriggeredFinancialAdvisorRemoval,
    updateSelectedUser,
    updateBreadcrumbLabel,
    updateSelectedFinancialAdvisorClients,
    resetAdviceAdminReducer,
    updateUsersByEmail
} = advisorAdminClientsTable.actions;

export default advisorAdminClientsTable.reducer;
